import React, { useState } from "react";

import { Box, Button, Typography, Paper } from "@mui/material";
import { MuiTelInput } from "mui-tel-input";
import { useTranslation } from "react-i18next";

interface PhoneInputCardProps {
  // Called with the cleaned number (e.g. "+16471112233") once it validates.
  onSubmit: (phoneNumber: string) => void;
}

const PhoneInputCard = ({ onSubmit }: PhoneInputCardProps) => {
  const { t } = useTranslation("message");
  const [phoneNumber, setPhoneNumber] = useState("+1");
  const [error, setError] = useState("");

  const handlePhoneChange = (newPhone: string) => {
    setPhoneNumber(newPhone);
    setError("");
  };

  const isValidPhone = () => {
    const cleaned = phoneNumber.replace(/[^\d+]/g, "");
    return /^\+1\d{10}$/.test(cleaned);
  };

  const handleStartChat = () => {
    if (!isValidPhone()) {
      setError(t("phoneInput.invalid"));
      return;
    }

    onSubmit(phoneNumber.replace(/[^\d+]/g, ""));
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && isValidPhone()) {
      handleStartChat();
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 3,
        overflowY: "auto",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          maxWidth: 500,
          width: "100%",
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          {t("phoneInput.heading")}
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
          {t("phoneInput.intro")}
        </Typography>

        <MuiTelInput
          value={phoneNumber}
          onChange={handlePhoneChange}
          onKeyPress={handleKeyPress}
          forceCallingCode={false}
          defaultCountry="CA"
          onlyCountries={["US", "CA", "IN", "GB"]}
          autoFocus
          variant="outlined"
          fullWidth
          label={t("phoneInput.label")}
          error={!!error}
          helperText={error || t("phoneInput.helperText")}
          disableDropdown={false}
          sx={{ mb: 3 }}
        />

        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleStartChat}
          disabled={!isValidPhone()}
          title={
            !isValidPhone()
              ? t("phoneInput.invalidTitle")
              : t("phoneInput.startConversation")
          }
        >
          {t("phoneInput.startChat")}
        </Button>
      </Paper>
    </Box>
  );
};

export default PhoneInputCard;
